import { useState } from "react";
import { type DocumentEntry } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, FileText, X, Download } from "lucide-react";

const DOC_TEMPLATES: Record<string, string> = {
  brd: `# Business Requirements Document (BRD)

## 1. Project Overview
**Project Name:** 
**Business Owner:** 
**BA Lead:** 
**Date:** 

## 2. Purpose & Background
<!-- Why this project exists, what business problem it solves -->

## 3. Scope
### In Scope
- 

### Out of Scope
- 

## 4. Stakeholders
| Name | Role | Team |
|------|------|------|
|      |      |      |

## 5. Business Requirements
| ID | Requirement | Priority | Acceptance Criteria |
|----|-------------|----------|---------------------|
| BR-001 |         |          |                     |

## 6. Assumptions
- 

## 7. Constraints
- 

## 8. Dependencies
- 

## 9. Risks
| Risk | Impact | Mitigation |
|------|--------|------------|
|      |        |            |

## 10. Sign-off
| Name | Role | Date | Signature |
|------|------|------|-----------|
|      |      |      |           |
`,
  frd: `# Functional Requirements Document (FRD)

## 1. Introduction
**Document Version:** 1.0
**Related BRD:** 

## 2. Functional Requirements

### FR-001: 
**Description:** 
**User Role:** 
**Input:** 
**Process:** 
**Output:** 
**Business Rules:**
- 

### FR-002:
**Description:** 
**User Role:** 
**Input:** 
**Process:** 
**Output:** 

## 3. Non-Functional Requirements
- **Performance:** 
- **Security:** 
- **Scalability:** 
- **Availability:** 

## 4. Data Requirements
<!-- Data flows, file formats (JSON, XML), backend processing -->

## 5. Interface Requirements
<!-- API specs, third-party integrations, HL7/FHIR if applicable -->

## 6. Error Handling
| Error Code | Description | User Message | Resolution |
|------------|-------------|--------------|------------|
|            |             |              |            |
`,
  srs: `# Software Requirements Specification (SRS)

## 1. System Overview
**System Name:** 
**Technology Stack:** 
**Architecture:** 

## 2. User Classes
| User Type | Description | Access Level |
|-----------|-------------|--------------|
|           |             |              |

## 3. System Features

### SF-001: 
**Priority:** Must Have
**Description:** 
**Stimulus/Response:**
1. User action → 
2. System response → 

### SF-002:
**Priority:** Should Have
**Description:** 

## 4. External Interface Requirements
### User Interfaces
### Hardware Interfaces
### Software Interfaces
### Communication Interfaces

## 5. Non-Functional Requirements
### Performance
### Security
### Reliability
### Maintainability

## 6. Compliance
<!-- HIPAA, FERPA, state-specific regulations, small cell suppression -->
`,
  notes: `# Meeting Notes

**Date:** 
**Attendees:** 
**Agenda:** 

## Discussion Points
1. 

## Action Items
- [ ] 

## Follow-up
- Next meeting: 
- Owner: 
`,
};

const typeLabels: Record<string, string> = {
  brd: "BRD",
  frd: "FRD",
  srs: "SRS",
  notes: "Notes",
};

interface Props {
  documents: DocumentEntry[];
  onChange: (documents: DocumentEntry[]) => void;
}

export default function DocumentEditor({ documents, onChange }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newType, setNewType] = useState<string>("");

  const handleAdd = () => {
    if (!newType) return;
    const doc: DocumentEntry = {
      id: crypto.randomUUID(),
      type: newType as any,
      title: `${typeLabels[newType]} - ${new Date().toLocaleDateString()}`,
      content: DOC_TEMPLATES[newType] || "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onChange([...documents, doc]);
    setActiveId(doc.id);
    setNewType("");
  };

  const updateDoc = (id: string, updates: Partial<DocumentEntry>) => {
    onChange(
      documents.map((d) =>
        d.id === id ? { ...d, ...updates, updatedAt: new Date().toISOString() } : d
      )
    );
  };

  const removeDoc = (id: string) => {
    onChange(documents.filter((d) => d.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const exportDoc = (doc: DocumentEntry) => {
    const blob = new Blob([doc.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.title.replace(/\s+/g, "-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeDoc = documents.find((d) => d.id === activeId);

  return (
    <div className="flex flex-col h-full">
      {/* Doc list header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
          Documents ({documents.length})
        </h2>
        <div className="flex gap-2">
          <Select value={newType} onValueChange={setNewType}>
            <SelectTrigger className="w-28 h-7 text-xs">
              <SelectValue placeholder="Type…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="brd">BRD</SelectItem>
              <SelectItem value="frd">FRD</SelectItem>
              <SelectItem value="srs">SRS</SelectItem>
              <SelectItem value="notes">Notes</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleAdd} disabled={!newType} className="h-7 gap-1 text-xs">
            <Plus className="h-3 w-3" /> New
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar list */}
        <div className="w-48 border-r border-border overflow-auto shrink-0">
          {documents.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground text-center">No documents</p>
          ) : (
            documents.map((doc) => (
              <button
                key={doc.id}
                onClick={() => setActiveId(doc.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-border text-xs font-body flex items-center gap-2 transition-colors ${
                  activeId === doc.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "hover:bg-secondary/50"
                }`}
              >
                <FileText className="h-3 w-3 shrink-0" />
                <div className="min-w-0">
                  <p className="truncate font-semibold">{doc.title}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {typeLabels[doc.type]}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col min-w-0">
          {activeDoc ? (
            <>
              <div className="px-4 py-2 border-b border-border flex items-center gap-2 shrink-0">
                <Input
                  value={activeDoc.title}
                  onChange={(e) => updateDoc(activeDoc.id, { title: e.target.value })}
                  className="h-7 text-xs font-semibold border-0 p-0 focus-visible:ring-0"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => exportDoc(activeDoc)}
                  className="h-7 px-2"
                >
                  <Download className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDoc(activeDoc.id)}
                  className="h-7 px-2 text-destructive"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <Textarea
                value={activeDoc.content}
                onChange={(e) => updateDoc(activeDoc.id, { content: e.target.value })}
                className="flex-1 border-0 rounded-none resize-none font-mono text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select or create a document
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
